import {ModalBody, ModalFooter, ModalHeader} from "@heroui/modal";
import {Button, ButtonGroup} from "@heroui/button";
import {Checkbox} from "@heroui/checkbox";
import {Tooltip} from "@heroui/tooltip";
import React, {useEffect} from "react";
import {Trim} from "@/components/Trim";
import {Divider} from "@heroui/divider";
import {Skeleton} from "@heroui/skeleton";
import {ScrollShadow} from "@heroui/react";
import type {types as hlsTypes} from "hls-parser";

import {errorMessageOf, fetchText, getPreferredExt, hlsParse, isDev, slugify} from "@/components/DownloaderApp/utils";
import throttle from 'lodash.throttle';

import {useMonitoring} from "@/components/monitoring";
import {getSubtitleTracks, sortVariantsByQuality} from "@/components/DownloaderApp/playlists";
import {LiveChip} from "@/components/DownloaderApp/LiveChip";
import {BaselinePlayCircle, DownloadIcon, InfoIcon} from "@/components/DownloaderApp/icons";
import {useFeatureFlagEnabled} from "@posthog/react";

const V3_REMUX_DURATION_THRESHOLD_SECONDS = 24 * 60 * 60;

const updatePreservedTrim = throttle((key, newSecsTrim) => {
    try {
        sessionStorage[slugify(key)] = JSON.stringify(newSecsTrim)
    } catch (err) {

    }
}, 400);

function formatBandwidth(bps) {
    if (bps >= 1_000_000) return (bps / 1_000_000).toFixed(2) + " Mbps";
    if (bps >= 1_000) return (bps / 1_000).toFixed(0) + " kbps";
    return bps + " bps";
}

function formatQuality(variant): string {
    if (variant.title) return variant.title;
    if (variant.resolution?.height) {
        return `${variant.resolution.height}p${variant.frameRate ? `${Math.round(variant.frameRate)}` : ''}`
    }
    if (variant.bandwidth) return formatBandwidth(variant.bandwidth);
    return 'Unknown';
}

// Some playlists (e.g. Rumble) ship several renditions with the same height,
// so show the bitrate below any label that would otherwise repeat.
function getQualityLabels(variants): Map<any, string[]> {
    const baseLabels = variants.map(formatQuality);
    const counts = baseLabels.reduce((acc, label) => acc.set(label, (acc.get(label) || 0) + 1), new Map());
    return new Map(variants.map((variant, index) => {
        const label = baseLabels[index];
        const bitrate = variant.bandwidth && formatBandwidth(variant.bandwidth);
        return [variant, counts.get(label) > 1 && bitrate && label !== bitrate ? [label, bitrate] : [label]];
    }));
}

export function ChooseQualityPopup({data, onAccept}) {

    sortVariantsByQuality(data.masterPlaylist)
    const defaultQuality = data.masterPlaylist.variants[0];
    const qualityLabels = getQualityLabels(data.masterPlaylist.variants);
    const hasBitrateLine = data.masterPlaylist.variants.some(variant => qualityLabels.get(variant).length > 1);
    const subtitleTracks = getSubtitleTracks(data.masterPlaylist, data.masterPlaylist._url);

    // isDev && console.log({data})

    // const isMe = useFeatureFlagEnabled('is_me');

    let showWatchCTA = !!data.details && data.video?.type === 'vod' && data.video.platform === 'kick';
    // showWatchCTA = false;

    const [quality, setQuality] = React.useState(defaultQuality);
    const [hqPlaylist, setHqPlaylist] = React.useState<any>(null);
    const [trim, setTrim] = React.useState<[number, number] | null>(data.trim);
    const [secTrim, setSecTrim] = React.useState<[number, number] | null>(data.secTrim);
    const [duration, setDuration] = React.useState<[number, number] | null>(null);
    const [ext, setExt] = React.useState<'.ts' | '.mp4' | '.mp3' | '.vtt'>(
        data.enforceExtenstion === 'vtt' && subtitleTracks.length ? '.vtt' : getPreferredExt()
    );
    const [remux, setRemux] = React.useState<boolean>(false);
    const [subtitleUrl, setSubtitleUrl] = React.useState(
        (subtitleTracks.find(subtitle => subtitle.isDefault) ?? subtitleTracks[0])?._url
    );
    const [isPreparingSubtitles, setIsPreparingSubtitles] = React.useState(false);
    const [subtitleError, setSubtitleError] = React.useState("");
    const selectedSubtitle = subtitleTracks.find(subtitle => subtitle._url === subtitleUrl);
    const isSubtitleDownload = ext === '.vtt';
    const extensions: Array<typeof ext> = [".mp4", ".ts", ".mp3"];
    if (subtitleTracks.length) extensions.push(".vtt");

    // The trim indices line up with the playlist segment indices, so slice the same way
    // the downloader does to judge only the segments actually being saved.
    const discontinuityPresent = React.useMemo(() => {
        const segments = hqPlaylist?.segments;
        if (!segments) return false;
        const selected = trim ? segments.slice(trim[0], trim[1]) : segments;
        return selected.some(segment => segment.discontinuitySequence !== 0);
    }, [hqPlaylist, trim]);
    const selectedDurationSeconds = React.useMemo(() => {
        const segments = hqPlaylist?.segments;
        if (!segments) return 0;
        const selected = trim ? segments.slice(trim[0], trim[1]) : segments;
        return selected.reduce((total, segment) => total + segment.duration, 0);
    }, [hqPlaylist, trim]);

    // Trimming to part of the playlist means only that piece gets saved, so call it a
    // fragment. A trim spanning every segment is still the whole video.
    const isFragment = React.useMemo(() => {
        if (!trim) return false;
        const segments = hqPlaylist?.segments;
        if (!segments) return true;
        return trim[0] > 0 || trim[1] < segments.length;
    }, [hqPlaylist, trim]);

    const {onClickTrack, track} = useMonitoring()

    async function onDownloadClicked() {
        if (isSubtitleDownload) {
            setSubtitleError("");
            setIsPreparingSubtitles(true);
            try {
                if (!selectedSubtitle) throw new Error("Choose a subtitle language.");
                let playlistText: string;
                if (data.video?.fetchWithExtension) {
                    if (!("sendMessage" in window) || typeof window.sendMessage !== "function") {
                        throw new Error("The browser extension is not connected.");
                    }
                    playlistText = await window.sendMessage("fetch-text", selectedSubtitle._url);
                } else {
                    playlistText = await fetchText(selectedSubtitle._url);
                }
                const subtitlePlaylist = hlsParse(playlistText);
                if (!("segments" in subtitlePlaylist) || !subtitlePlaylist.segments.length) {
                    throw new Error("The selected subtitle playlist has no segments.");
                }

                let subtitleTrim: [number, number] | null = null;
                if (trim) {
                    if (!hqPlaylist) throw new Error("The video playlist is still loading.");
                    const fromSeconds = hqPlaylist.segments.slice(0, trim[0])
                        .reduce((total: number, segment: hlsTypes.Segment) => total + segment.duration, 0);
                    const toSeconds = hqPlaylist.segments.slice(0, trim[1])
                        .reduce((total: number, segment: hlsTypes.Segment) => total + segment.duration, 0);
                    let elapsed = 0;
                    let from = 0;
                    let to = 0;
                    subtitlePlaylist.segments.forEach((segment: hlsTypes.Segment, index: number) => {
                        if (elapsed < toSeconds) to = index + 1;
                        elapsed += segment.duration;
                        if (elapsed <= fromSeconds) from = index + 1;
                    });
                    if (from >= to) throw new Error("No subtitles are available in the selected trim.");
                    subtitleTrim = [from, to];
                }

                onAccept({
                    quality: selectedSubtitle,
                    formatQuality: selectedSubtitle.name,
                    duration,
                    trim: subtitleTrim,
                    ext,
                    remux: null,
                });
            } catch (err) {
                console.error("Failed to prepare subtitle download", err);
                setSubtitleError(errorMessageOf(err));
            } finally {
                setIsPreparingSubtitles(false);
            }
            return;
        }
        let selectedQuality = quality
        if(ext === '.mp3') {
            // selectedQuality = data.masterPlaylist.variants[data.masterPlaylist.variants.length - 1];
        }

        let newRemux = null;

        if(!remux) {
            newRemux = null
        } else if(selectedDurationSeconds > V3_REMUX_DURATION_THRESHOLD_SECONDS) {
            newRemux = 'v3'
        } else if(discontinuityPresent) {
            newRemux = 'v2'
        }

        onAccept({
            quality: selectedQuality,
            formatQuality: qualityLabels.get(selectedQuality).join(" "),
            duration,
            trim,
            ext,
            remux: newRemux
        })
    }

    // Re-derive whenever the selected segments change, so changing the trim always
    // reflects whether that particular range needs fixing.
    useEffect(() => {
        setRemux(discontinuityPresent || (selectedDurationSeconds > V3_REMUX_DURATION_THRESHOLD_SECONDS));
    }, [hqPlaylist, trim, discontinuityPresent, selectedDurationSeconds]);

    useEffect(() => {
        data.hqPlaylist.then(setHqPlaylist)
    }, [])

    useEffect(() => {
        if(ext === '.mp3') {
            setQuality(data.masterPlaylist.variants[data.masterPlaylist.variants.length - 1])
        }
    }, [ext])

    useEffect(() => {
        fetchText(quality._url).catch(err => console.error(err))
        if(defaultQuality != quality) {

        }
    }, [quality])

    useEffect(() => {
        if (!trim || !data.masterPlaylist._url) return;
        updatePreservedTrim(data.masterPlaylist._url, trim)
    }, [trim]);

    useEffect(() => {
        if(ext === '.mp3' || ext === '.vtt') return;
        try {
            localStorage.ext = ext
        } catch (err) {
        }
    }, [ext]);

    // console.log(data.masterPlaylist.variants)

    return (
        <>
            <ModalHeader className="flex flex-col gap-1">
        <span id="modal-title">
          Customize download
        </span>
            </ModalHeader>
            <ModalBody className={"py-4 px-3 md:px-5"}>
                <div className={"flex flex-col justify-between gap-3 md:flex-row md:gap-8"}>
                    <div className={"flex min-w-0 max-w-full flex-1 flex-col gap-2"}>
                        <h2 className={"text-xl"}>{isSubtitleDownload ? "Choose language" : "Choose quality"}</h2>
                        <ScrollShadow orientation="horizontal" size={24} className={"max-w-full"}>
                            <ButtonGroup className={"min-w-max"}>
                                {isSubtitleDownload ? subtitleTracks.map(subtitle => (
                                    <Button
                                        key={subtitle._url}
                                        onPress={() => {
                                            setSubtitleUrl(subtitle._url);
                                            setSubtitleError("");
                                        }}
                                        color={subtitle._url === subtitleUrl ? "primary" : "default"}
                                        isDisabled={isPreparingSubtitles}
                                        variant="solid">
                                        {subtitle.name}
                                    </Button>
                                )) : data.masterPlaylist.variants.map((variant, index) => (
                                    <Button
                                        // onPress={() => onAccept(variant)}
                                        onPress={(e) => {
                                            // onClickTrack(`Quality change`, {variant})(e)
                                            setQuality(variant)
                                        }}
                                        key={index}
                                        // color={Math.round(variant.frameRate) === 60 ? "primary" : "default"}
                                        color={variant === quality ? "primary" : "default"}
                                        className={hasBitrateLine ? "h-14 px-5" : "px-5"}
                                        variant={"solid"}>
                                        <div className={"flex flex-col items-center"}>
                                            {qualityLabels.get(variant).map((line, lineIndex) => (
                                                <span key={lineIndex} className={lineIndex === 1 ? "text-[10px] opacity-70" : undefined}>{line}</span>
                                            ))}
                                        </div>
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </ScrollShadow>
                    </div>
                    <div className={"flex shrink-0 flex-col gap-2"}>
                        <h2 className={"text-xl "}>File format</h2>
                        <ButtonGroup className={"flex"}>
                            {extensions.map((extension, index) => (
                                <Tooltip
                                    key={index}
                                    content="VOD subtitles"
                                    isDisabled={extension !== ".vtt"}>
                                    <Button
                                        onPress={(e) => {
                                            // onClickTrack(`Format change`, {extension})(e);
                                            setExt(extension)
                                            setSubtitleError("")
                                        }}
                                        color={extension === ext ? "primary" : "default"}
                                        isDisabled={isPreparingSubtitles}
                                        variant={"solid"}>
                                        {extension}
                                    </Button>
                                </Tooltip>
                            ))}
                        </ButtonGroup>
                    </div>
                </div>

                <Divider className={"mt-5"}/>
                <h2 className="text-xl flex content-center gap-2">
                    <span>Trim</span>
                    {data.video?.type === 'live' && <LiveChip />}
                </h2>
                <div className="min-h-24 flex justify-center items-center">

                    {hqPlaylist ?
                        <div className={"w-full"}><Trim playlist={hqPlaylist} masterPlaylistUrl={data.masterPlaylist._url}
                                                        video={data.video} trim={trim} secTrim={secTrim}
                                                        onTrimChanged={(newTrim, newSecsTrim, duration) => {
                                                            setTrim(newTrim)
                                                            setDuration(duration)
                                                        }}/>
                        </div> : <div className="w-full flex flex-col">
                            <Skeleton className="h-3 w-28 mb-3 self-end rounded-md"/>
                            <Skeleton className="h-7 rounded-md"/>
                            <div className="pt-3 flex justify-between">
                                <Skeleton className="h-3 w-72 rounded-md"/>
                                <Skeleton className="h-3 w-56 rounded-md"/>
                            </div>
                        </div>}
                </div>
                {!isSubtitleDownload && <>
                    <Divider className={"mt-5"}/>
                    <div className={"flex items-center gap-1.5"}>
                        <Checkbox
                            isSelected={remux}
                            onValueChange={(selected) => {
                                setRemux(selected);
                            }}
                        >
                            Try to fix this {isFragment ? "video fragment" : "video"}
                        </Checkbox>
                        <Tooltip
                            content={"Interrupted streams can leave broken timestamps, so the saved file shows the wrong length and seeking jumps to the wrong place. Turning this on fixes the timing while the video downloads."}
                            className={"max-w-xs"}
                        >
                            <span className={"cursor-help text-default-500"} tabIndex={0} aria-label={"More information"}>
                                <InfoIcon className={"text-lg"}/>
                            </span>
                        </Tooltip>
                    </div>
                </>}
                {isSubtitleDownload && subtitleError && <p role="alert" className="text-danger">{subtitleError}</p>}
                <Button color="primary" className={"mt-5"} onPress={onDownloadClicked} startContent={<DownloadIcon/>}
                        isDisabled={isSubtitleDownload && !selectedSubtitle}
                        isLoading={isPreparingSubtitles}>
                    <span>Download ({[ext, isSubtitleDownload ? selectedSubtitle?.name : qualityLabels.get(quality).join(" "), duration].filter(a => !!a).join(", ")})</span>
                </Button>
                {
                    showWatchCTA && <Button color="default" className={""} onPress={() => {
                        try {
                            localStorage['vod-'+encodeURIComponent(data.video.url)] = data.masterPlaylist._url
                        } catch (err) {
                        }
                        window.open(`/play?url=${encodeURIComponent(data.video.url)}`)
                    }} startContent={<BaselinePlayCircle />}>
                        Watch this video now
                    </Button>
                }

            </ModalBody>
            <ModalFooter>
            </ModalFooter>
        </>
    );
}